import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useGetObjectives, useLoadObjectiveBranch } from "./hooks";
import { GOALS_ACTIONS } from "./actionTypes";
import { goalsReducer, initialState } from "./reducers";

const GoalsExploreContext = createContext();

export const useGoalsExploreContext = () => {
  const context = useContext(GoalsExploreContext);
  if (!context) {
    throw new Error(
      "useGoalsExploreContext must be used within GoalsExploreProvider"
    );
  }
  return context;
};

const GoalsExploreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(goalsReducer, initialState);
  const [loadingNodeIds, setLoadingNodeIds] = useState({});

  const handleRootDataLoaded = useCallback((data) => {
    const objectives = data?.searchObjectives?.objectives || [];

    dispatch({
      type: GOALS_ACTIONS.LOAD_ROOT_OBJECTIVES,
      payload: { objectives },
    });
  }, []);

  const handleBranchDataLoaded = useCallback((data, parentNodeId) => {
    const objectives = data?.searchObjectives?.objectives || [];

    // Update loading state
    setLoadingNodeIds((prev) => {
      const newState = { ...prev };
      delete newState[parentNodeId];
      return newState;
    });

    dispatch({
      type: GOALS_ACTIONS.LOAD_BRANCH_OBJECTIVES,
      payload: { objectives, parentNodeId },
    });
  }, []);

  const { loading: queryLoading, error: queryError } = useGetObjectives({
    onCompleted: handleRootDataLoaded,
  });

  const [loadBranchQuery, { loading: branchLoading }] =
    useLoadObjectiveBranch();

  const loadBranch = useCallback(
    (nodeId) => {
      // Set loading state for this node
      setLoadingNodeIds((prev) => ({ ...prev, [nodeId]: true }));

      // Load the branch data
      loadBranchQuery({
        objectiveIds: [nodeId],
        maxChildDepth: 1,
      }).then((result) => {
        if (result.data) {
          handleBranchDataLoaded(result.data, nodeId);
        }
      });
    },
    [loadBranchQuery, handleBranchDataLoaded]
  );

  const contextValue = {
    ...state,
    loading: state.loading || queryLoading || branchLoading,
    error: state.error || queryError,
    loadBranch,
    loadingNodeIds,
    childrenMap: state.childrenMap,
    dispatch,
  };

  return (
    <GoalsExploreContext.Provider value={contextValue}>
      {children}
    </GoalsExploreContext.Provider>
  );
};

GoalsExploreProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default GoalsExploreProvider;
